import React, { useState, useEffect, useRef } from 'react';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { TextField, InputAdornment, MenuItem } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactPixel from 'react-facebook-pixel';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimePickerGridSelect from './TimePickerGridSelect';
import { format, addDays } from 'date-fns';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import { staticLocationsFromSearch } from '@/helpers/locationHelpers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const fetchUserLocation = async () => {
  try {
    const response = await fetch('http://ip-api.com/json/');
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    const data = await response.json();
    return { lat: data.lat, lng: data.lon };
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

const validationSchema = Yup.object().shape({
  location: Yup.string().required('El lugar es obligatorio'),
  startDate: Yup.date().required('La fecha es obligatoria'),
  //endTime: Yup.string().required('El horario es obligatorio'),
  guests: Yup.number()
    .min(1, 'Debe haber al menos 1 persona')
    .required('Cantidad de personas requerida'),
});

const NewGeneralSearchForm = props => {
  const { searchType } = props;
  const [userLocation, setUserLocation] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [customDateError, setCustomDateError] = useState('');

  const autocompleteService = useRef(null);
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const lenguage = useSelector(state => state.lenguage?.lang);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setSelectedSuggestionFromGoogleService = value => {
    setSelectedSuggestion(value);
    if (value?.place_id && window.google) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div'),
      );
      service.getDetails({ placeId: value?.place_id }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const newCoordinates = place.geometry.location.toJSON();
          setCoordinates(newCoordinates);
        }
      });
    }
  };

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    };

    fetchUserLocation().then(location => setUserLocation(location));

    if (!window.google) {
      window.addEventListener('load', initializeAutocomplete);
      return () => window.removeEventListener('load', initializeAutocomplete);
    } else {
      initializeAutocomplete();
      setSelectedSuggestionFromGoogleService(
        lenguage === 'ES'
          ? staticLocationsFromSearch[1]
          : staticLocationsFromSearch[0],
      );
    }
  }, []);

  const initialValues = {
    location:
      lenguage === 'ES'
        ? staticLocationsFromSearch[1]?.description
        : staticLocationsFromSearch[0]?.description,
    startDate: tomorrow,
    startTime: '10:00',
    endTime: '11:00',
    guests: 1,
  };

  const handleSubmit = values => {
    const spaceType = searchType === 'Sala' ? 'sala de juntas' : 'coworking';
    const searchFormData = {
      ...values,
      coordinates,
      selectedSuggestion,
      spaceType,
    };
    dispatch(setSearchFormData(searchFormData));
    ReactPixel.trackCustom('submitSearchForm', { clave: searchFormData });
    navigate(lenguage === 'ES' ? '/lista-espacios' : '/spaces-list');
  };

  return (
    <FlexContainer className={'flex-column m-marglr10'}>
      <FlexContainer className={'format100 '}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const setSelectedSuggestionxCBO = value => {
              setSelectedSuggestionFromGoogleService(value);
              setFieldValue('location', value?.description);
            };

            return (
              <Form className={'width100 padlr10'}>
                <FlexContainer className={'width100 justify between gap-2'}>
                  <TextField
                    fullWidth
                    select
                    label={lenguage === 'ES' ? 'Lugar' : 'Location'}
                    value={values.location}
                    onChange={e => setFieldValue('location', e.target.value)}
                    SelectProps={{
                      renderValue: selected => {
                        // evita que muestre la imagen al seleccionar
                        const selectedOption = staticLocationsFromSearch?.find(
                          s => s.description === selected,
                        );
                        return selectedOption ? selectedOption.description : '';
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        paddingTop: '18px',
                        paddingLeft: '44px',
                        color: '#F985C4',
                        fontWeight: 600,
                      },
                    }}
                    InputProps={{
                      notched: false,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon sx={{ color: '#3e5695' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#3e5695',
                        borderRadius: '12px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8f8c8e',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8f8c8e',
                        },
                        '& .MuiOutlinedInput-input': {
                          paddingRight: '32px',
                          paddingTop: '26px',
                          paddingBottom: '8px',
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: '10px' }}>
                      {lenguage === 'ES'
                        ? 'Selecciona un lugar'
                        : 'Select a location'}
                    </MenuItem>

                    {staticLocationsFromSearch?.map(suggestion => (
                      <MenuItem
                        onClick={() => setSelectedSuggestionxCBO(suggestion)}
                        key={suggestion?.place_id}
                        value={suggestion?.description}
                        sx={{
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={suggestion?.icon}
                          alt="icon"
                          style={{
                            width: '40px',
                            height: '40px',
                            marginRight: '8px',
                            objectFit: 'contain',
                          }}
                        />
                        {lenguage === 'ES'
                          ? suggestion?.description
                          : suggestion?.description_en}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    type="date"
                    label={lenguage === 'ES' ? 'Día' : 'Date'}
                    value={values.startDate}
                    error={customDateError !== ''}
                    onClick={e => {
                      const input = e.currentTarget.querySelector('input');
                      if (input) {
                        try {
                          input.showPicker?.();
                        } catch (err) {
                          input.focus();
                        }
                      }
                    }}
                    onChange={e => {
                      const selectedDate = e.target.value;
                      if (selectedDate >= tomorrow) {
                        setFieldValue('startDate', selectedDate);
                        setCustomDateError('');
                      } else {
                        setCustomDateError(
                          'Debe ingresar una fecha posterior al día actual',
                        );
                      }
                    }}
                    autoFocus
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        locale: 'es-ES',
                        paddingTop: '18px',
                        paddingLeft: '44px',
                        color: '#F985C4',
                        fontWeight: 600,
                      },
                    }}
                    InputProps={{
                      inputProps: {
                        min: tomorrow,
                        pattern: '[0-9]{4}-[0-9]{2}-[0-9]{2}',
                      },
                      notched: false,
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          onClick={e => {
                            e.stopPropagation();
                            const input = e.currentTarget
                              .closest('.MuiInputBase-root')
                              ?.querySelector('input');
                            if (input) input.showPicker?.();
                          }}
                        >
                          <CalendarTodayOutlinedIcon
                            sx={{ color: '#3e5695' }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#3e5695',
                        borderRadius: '12px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8f8c8e',
                        },
                        '& .MuiOutlinedInput-input': {
                          paddingRight: '32px',
                          paddingTop: '26px',
                          paddingBottom: '8px',
                          cursor: 'pointer',
                        },
                        '& input[type="date"]::-webkit-calendar-picker-indicator':
                          {
                            filter: 'invert(1)',
                            opacity: 1,
                          },
                      },
                    }}
                  ></TextField>

                  <TimePickerGridSelect
                    label={lenguage === 'ES' ? 'Inicio' : 'Start'}
                    value={values.startTime}
                    onChange={val => setFieldValue('startTime', val)}
                    disabledHours={['07:00', '20:00', '21:00']}
                  />

                  <TimePickerGridSelect
                    label={lenguage === 'ES' ? 'Final' : 'End'}
                    value={values.endTime}
                    onChange={val => setFieldValue('endTime', val)}
                    disabledHours={['07:00', '20:00', '21:00']}
                  />

                  <TextField
                    fullWidth
                    type="number"
                    label={lenguage === 'ES' ? 'Personas' : 'People'}
                    value={values.guests}
                    onChange={e => setFieldValue('guests', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        paddingTop: '18px',
                        paddingLeft: '44px',
                        color: '#F985C4',
                        fontWeight: 600,
                      },
                    }}
                    InputProps={{
                      notched: false,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineOutlinedIcon
                            sx={{ color: '#3e5695' }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#3e5695',
                        borderRadius: '12px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8f8c8e',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8f8c8e',
                        },
                        '& .MuiOutlinedInput-input': {
                          paddingRight: '32px',
                          paddingTop: '26px',
                          paddingBottom: '8px',
                        },
                      },
                    }}
                  ></TextField>

                  <button
                    className={
                      'bgBlue400 border-rad8px borderNone fontWhite grayShadow16 m-format100 padlr10 text-14'
                    }
                    type="submit"
                  >
                    <strong>
                      {lenguage === 'ES' ? 'Reservar' : 'Reserve'}
                    </strong>
                  </button>
                </FlexContainer>
              </Form>
            );
          }}
        </Formik>
      </FlexContainer>
    </FlexContainer>
  );
};

const NewHeaderSearchComponent = () => {
  const [showHeader, setShowHeader] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setShowHeader(window.scrollY < 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!showHeader) return null;
  return (
    <FlexContainer
      style={{ zIndex: 4, left: '50%', transform: 'translateX(-50%)' }}
      className={
        'FlexContainer grayShadowBottom bgTransparentPurple border2pxPurple8 border-rad8px flex-column flex-container format60 m-hide margt70 padtb8 pos-fixed'
      }
    >
      <NewGeneralSearchForm searchType={'Sala'} />
    </FlexContainer>
  );
};

export default NewHeaderSearchComponent;
